/**
 * BudgetFooter Component
 * Totals row for budget table
 */

import React from 'react';
import VarianceIndicator from './VarianceIndicator';

export interface BudgetFooterProps {
  totalOriginalBudget: number;
  totalApprovedChanges: number;
  totalRevisedBudget: number;
  totalCommitted: number;
  totalActual: number;
  totalForecast: number;
  totalVariance: number;
  totalVariancePercent: number;
}

const BudgetFooter: React.FC<BudgetFooterProps> = ({
  totalOriginalBudget,
  totalApprovedChanges,
  totalRevisedBudget,
  totalCommitted,
  totalActual,
  totalForecast,
  totalVariance,
  totalVariancePercent,
}) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <tfoot className="bg-gray-100 border-t-2 border-gray-300 sticky bottom-0">
      <tr className="font-bold">
        {/* Label */}
        <td colSpan={2} className="px-4 py-4 text-sm text-gray-900">
          TOTALS
        </td>

        {/* Original Budget */}
        <td className="px-4 py-4 text-sm text-gray-900 text-right whitespace-nowrap">
          {formatCurrency(totalOriginalBudget)}
        </td>

        {/* Approved Changes */}
        <td className="px-4 py-4 text-sm text-right whitespace-nowrap">
          {totalApprovedChanges !== 0 && (
            <span className={totalApprovedChanges > 0 ? 'text-green-600' : 'text-red-600'}>
              {totalApprovedChanges > 0 ? '+' : ''}
              {formatCurrency(totalApprovedChanges)}
            </span>
          )}
          {totalApprovedChanges === 0 && <span className="text-gray-400">-</span>}
        </td>

        {/* Revised Budget */}
        <td className="px-4 py-4 text-sm text-gray-900 text-right whitespace-nowrap">
          {formatCurrency(totalRevisedBudget)}
        </td>

        {/* Committed */}
        <td className="px-4 py-4 text-sm text-gray-900 text-right whitespace-nowrap">
          {formatCurrency(totalCommitted)}
        </td>

        {/* Actual */}
        <td className="px-4 py-4 text-sm text-gray-900 text-right whitespace-nowrap">
          {formatCurrency(totalActual)}
        </td>

        {/* Forecast */}
        <td className="px-4 py-4 text-sm text-gray-900 text-right whitespace-nowrap">
          {formatCurrency(totalForecast)}
        </td>

        {/* Variance */}
        <td className="px-4 py-4 text-sm text-right whitespace-nowrap">
          <VarianceIndicator
            variance={totalVariance}
            variancePercent={totalVariancePercent}
            size="md"
          />
        </td>

        {/* % Used - Empty for totals */}
        <td className="px-4 py-4"></td>

        {/* Actions - Empty for totals */}
        <td className="px-4 py-4"></td>
      </tr>
    </tfoot>
  );
};

export default BudgetFooter;
